import { DataButton } from "@sellar-io/components";
import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import { TwoBillingPeriodSummaryResponseType } from "@sellar-io/api-v2/types";
import { TwoSellarCreditPayStatementDialog } from "../../../SellarCreditTab/TwoSellarCreditTab/components/TwoSellarCreditPayStatementDialog";

export const SellarCreditStatementStats = ({
  statementMonth,
  billingPeriodSummary,
}: {
  statementMonth: Date;
  billingPeriodSummary: TwoBillingPeriodSummaryResponseType;
}) => {
  const [openPayStatementDialog, setOpenPayStatementDialog] = useState(false);
  const { dueDate, totals, status, paymentReference } = billingPeriodSummary;
  const outstandingTotal = totals?.outstandingAmount;
  const invoicedTotal = totals?.invoicedAmount;
  const formattedDueDate = dueDate
    ? dayjs(dueDate).format("MMMM D, YYYY")
    : "-";

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  return (
    <>
      <div
        className={twMerge(
          "flex w-full items-center divide-x overflow-x-auto bg-white py-4 md:mt-6",
          isDesktop && "rounded-lg border border-zinc-300",
        )}
      >
        <StatItem title="Statement">
          <span className="text-xl text-nowrap">
            <FormattedStatementTitle statementMonth={statementMonth} />
          </span>
        </StatItem>
        <StatItem title="Due date">
          <span className="text-xl text-nowrap">{formattedDueDate}</span>
        </StatItem>
        <StatItem title="Total to pay">
          <span className="text-xl text-nowrap">
            {outstandingTotal && invoicedTotal ? (
              <FormattedTotalToPay
                outstandingTotal={outstandingTotal}
                invoicedTotal={invoicedTotal}
              />
            ) : (
              "-"
            )}
          </span>
        </StatItem>
        <StatItem title="Payment status">
          <span className="text-xl text-nowrap">{status}</span>
        </StatItem>

        {!!outstandingTotal && (
          <StatItem title="Actions">
            <div className="flex gap-2">
              {outstandingTotal > 0 && (
                <PayStatementButton
                  onClick={() => setOpenPayStatementDialog(true)}
                />
              )}
            </div>
          </StatItem>
        )}
      </div>

      {!!(
        dueDate &&
        outstandingTotal &&
        outstandingTotal > 0 &&
        paymentReference
      ) && (
        <TwoSellarCreditPayStatementDialog
          active={openPayStatementDialog}
          onDismiss={() => setOpenPayStatementDialog(false)}
          dueDate={dueDate}
          amountOwed={outstandingTotal}
          paymentReference={paymentReference}
        />
      )}
    </>
  );
};

const StatItem = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => {
  return (
    <div className="flex flex-1 flex-col gap-2 px-4">
      <span className="flex items-center text-xs font-semibold text-nowrap">
        {title}
      </span>
      {children}
    </div>
  );
};

const FormattedTotalToPay = ({
  outstandingTotal,
  invoicedTotal,
}: {
  outstandingTotal: number;
  invoicedTotal: number;
}) => {
  if (outstandingTotal === invoicedTotal) {
    return <>{formatCurrency(invoicedTotal)}</>;
  }

  return (
    <>
      {formatCurrency(outstandingTotal)}
      <span className="text-sm font-normal">
        / {formatCurrency(invoicedTotal)}
      </span>
    </>
  );
};

const FormattedStatementTitle = ({
  statementMonth,
}: {
  statementMonth: Date;
}) => {
  const lastDay = dayjs(statementMonth).daysInMonth();
  const month = dayjs(statementMonth).format("MMMM");
  const year = dayjs(statementMonth).format("YYYY");

  return `${month} 1-${lastDay}, ${year}`;
};

const PayStatementButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <DataButton border onClick={onClick} className="pr-2">
      Pay statement
    </DataButton>
  );
};
